import * as React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { ArrowLeft, ChevronDown, Search } from "lucide-react";
import { filterSkillsByQuery, filterSkillsBySource } from "../skills/catalog";
import { SkillsClientError } from "../skills/client";
import {
  MAX_SELECTED_SKILLS,
  type SelectedSkillReference,
  type SkillSourceFilterValue,
  type SkillSummary,
  type SkillViewStatus,
} from "../skills/types";

export type SkillsBrowserCloseReason = "dismiss" | "escape" | "back" | "select" | "explore";

export interface SkillsBrowserPanelProps {
  open: boolean;
  status: SkillViewStatus;
  skills: readonly SkillSummary[];
  selectedSkills: readonly SelectedSkillReference[];
  search: string;
  sourceFilter: SkillSourceFilterValue;
  onSearchChange: (value: string) => void;
  onSourceFilterChange: (value: SkillSourceFilterValue) => void;
  onSelect: (skill: SkillSummary) => void;
  onExploreSkills: () => void;
  onOpenChange: (open: boolean, reason?: SkillsBrowserCloseReason) => void;
  /**
   * The composer's own disabled state, true while a query or enhance runs.
   * The attachment trigger goes dead at the same moment, so the panel has to
   * dismiss itself or it strands a catalog over the run.
   */
  disabled?: boolean;
  /**
   * Element the panel is positioned against — the attachment picker's menu
   * root. Mirrors `UserFileSelector`, which anchors to its own trigger.
   */
  anchorRef: React.RefObject<HTMLElement | null>;
  catalogError?: Error | null;
  onRetry?: () => void;
}

const PANEL_WIDTH_PX = 326;
const PANEL_HEIGHT_PX = 404;
const PANEL_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 16;

const SOURCE_FILTER_LABELS: Record<SkillSourceFilterValue, string> = {
  all: "All",
  personal: "My Skills",
  system: "System Skills",
};

const SOURCE_FILTER_ORDER: readonly SkillSourceFilterValue[] = ["all", "personal", "system"];

/**
 * Upstream text reaches us through the skills client, which relays whatever
 * the server said — strings like "skills upstream unavailable" are written for
 * operators, not for someone trying to pick a skill. So the status decides the
 * copy, and the detail goes to the console instead.
 */
export function getLoadErrorMessage(error: unknown): string {
  if (error instanceof SkillsClientError) {
    if (error.kind === "auth" || error.status === 403) {
      return "You do not have access to Skills.";
    }
    if (error.kind === "rate_limit" || error.status === 429) {
      return "Too many requests right now. Wait a moment, then retry.";
    }
    if (error.kind === "network") {
      return "Could not reach Skills. Check your connection, then retry.";
    }
    return "Skills are temporarily unavailable. Please retry in a moment.";
  }
  if (error instanceof TypeError) {
    return "Could not reach Skills. Check your connection, then retry.";
  }
  return "Skills are temporarily unavailable. Please retry in a moment.";
}

/**
 * Top-right source control. Hand-rolled for the same reason the attachment
 * menu is: the taskpane panels are portalled at a very high z-index, and a
 * library popover layered on top of that stacking context misplaces itself.
 */
const SourceFilterMenu: React.FC<{
  value: SkillSourceFilterValue;
  onChange: (value: SkillSourceFilterValue) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    menuRef.current
      ?.querySelector<HTMLButtonElement>('[role="menuitemradio"][aria-checked="true"]')
      ?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // Items are not tabbable, so Tab moves focus out of the dropdown while it
    // stays open. A later Escape would then reach only the panel's listener
    // and dismiss the whole browser, taking the search with it.
    const handleFocusIn = (event: FocusEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [open]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Escape belongs to the innermost layer. Without stopping propagation the
    // panel's document listener also fires and dismisses the whole browser,
    // discarding the search along with it.
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')?.focus();
      return;
    }

    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []
    );
    if (items.length === 0) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    let nextItem: HTMLButtonElement | undefined;
    switch (event.key) {
      case "ArrowDown":
        nextItem = items[(currentIndex + 1) % items.length];
        break;
      case "ArrowUp":
        nextItem = items[currentIndex <= 0 ? items.length - 1 : currentIndex - 1];
        break;
      case "Home":
        nextItem = items[0];
        break;
      case "End":
        nextItem = items[items.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    nextItem?.focus();
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Filter skills: ${SOURCE_FILTER_LABELS[value]}`}
        onClick={() => setOpen((current) => !current)}
        className="h-8 cursor-pointer gap-1 px-2 text-xs"
      >
        {SOURCE_FILTER_LABELS[value]}
        <ChevronDown className="h-3 w-3" aria-hidden />
      </Button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Filter skills"
          onKeyDown={handleKeyDown}
          className="absolute right-0 top-full z-10 mt-1 flex min-w-[136px] flex-col gap-1 rounded-lg border border-border bg-background p-1 shadow-lg"
        >
          {SOURCE_FILTER_ORDER.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === value}
              tabIndex={-1}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className={cn(
                "flex h-8 w-full cursor-pointer items-center whitespace-nowrap rounded-md border-0 bg-transparent px-2 text-left text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus",
                option === value && "bg-accent"
              )}
            >
              {SOURCE_FILTER_LABELS[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Skills browser opened from the composer's attachment menu.
 *
 * Search and source filtering stay client-side. The catalog is owned by the
 * shared skills controller so selecting a row, Explore, and chips share one
 * list. Rows are labelled by slug because that is the token the model consumes.
 */
export const SkillsBrowserPanel: React.FC<SkillsBrowserPanelProps> = ({
  open,
  status,
  skills,
  selectedSkills,
  search,
  sourceFilter,
  onSearchChange,
  onSourceFilterChange,
  onSelect,
  onExploreSkills,
  onOpenChange,
  disabled = false,
  anchorRef,
  catalogError,
  onRetry,
}) => {
  const [panelPosition, setPanelPosition] = useState<{
    side: "top" | "bottom";
    left: number;
    verticalOffset: number;
    width: number;
    height: number;
  }>({
    side: "top",
    left: VIEWPORT_PADDING_PX,
    verticalOffset: VIEWPORT_PADDING_PX,
    width: PANEL_WIDTH_PX,
    height: PANEL_HEIGHT_PX,
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedIds = useMemo(
    () => new Set(selectedSkills.map((skill) => skill.id)),
    [selectedSkills]
  );
  const selectedSlugs = useMemo(
    () => new Set(selectedSkills.map((skill) => skill.slug)),
    [selectedSkills]
  );
  const selectionAtLimit = selectedSkills.length >= MAX_SELECTED_SKILLS;

  const visibleSkills = useMemo(
    () => filterSkillsByQuery(filterSkillsBySource(skills, sourceFilter), search),
    [search, skills, sourceFilter]
  );

  const close = useCallback(
    (reason: SkillsBrowserCloseReason) => {
      onOpenChange(false, reason);
    },
    [onOpenChange]
  );

  // Focus only when the panel opens. `close` changes whenever hosts pass a
  // fresh onOpenChange, and putting focus in that effect stole it from the
  // source filter (and closed it via focusin) on catalog/hydration rerenders.

  useEffect(() => {
    if (disabled && open) close("dismiss");
  }, [close, disabled, open]);

  useEffect(() => {
    if (open && status === "error" && catalogError) {
      console.warn("[Skills] Failed to load the skills catalog", catalogError);
    }
  }, [catalogError, open, status]);

  useEffect(() => {
    if (!open) return undefined;
    const timeoutId = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !anchorRef.current?.contains(target)) {
        close("dismiss");
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close("escape");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, close, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePanelPosition = () => {
      const triggerRect = anchorRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const width = Math.min(
        PANEL_WIDTH_PX,
        Math.max(0, window.innerWidth - VIEWPORT_PADDING_PX * 2)
      );
      const availableAbove = triggerRect.top - PANEL_GAP_PX - VIEWPORT_PADDING_PX;
      const availableBelow =
        window.innerHeight - triggerRect.bottom - PANEL_GAP_PX - VIEWPORT_PADDING_PX;
      // The composer sits at the bottom of the taskpane, so prefer opening
      // upwards and only drop below when there genuinely is more room there.
      const side =
        availableAbove >= PANEL_HEIGHT_PX || availableAbove >= availableBelow ? "top" : "bottom";
      // Measured against the room on the chosen side rather than the whole
      // viewport. Excel's taskpane stacks extra chrome under the composer, so
      // the space above the trigger is smaller than the pane, and a full-height
      // panel would hang off the top with its search row clipped.
      const height = Math.max(
        0,
        Math.min(PANEL_HEIGHT_PX, side === "top" ? availableAbove : availableBelow)
      );
      const maxLeft = Math.max(
        VIEWPORT_PADDING_PX,
        window.innerWidth - VIEWPORT_PADDING_PX - width
      );

      setPanelPosition({
        side,
        left: Math.min(Math.max(VIEWPORT_PADDING_PX, triggerRect.left), maxLeft),
        verticalOffset:
          side === "top"
            ? window.innerHeight - triggerRect.top + PANEL_GAP_PX
            : triggerRect.bottom + PANEL_GAP_PX,
        width,
        height,
      });
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [anchorRef, open]);

  if (!open) return null;

  const renderContent = () => {
    if (status === "idle" || status === "loading") {
      return (
        <div className="flex flex-1 items-center justify-center px-4 text-xs text-muted-foreground">
          Loading Skills…
        </div>
      );
    }

    if (status === "error") {
      const message = getLoadErrorMessage(catalogError);
      return (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center"
          role="alert"
        >
          <p className="m-0 text-xs text-muted-foreground">{message}</p>
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="cursor-pointer"
            >
              Retry
            </Button>
          )}
        </div>
      );
    }

    if (skills.length === 0) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
          <p className="m-0 text-xs font-medium text-foreground">No skills available yet</p>
          <p className="m-0 text-xs text-muted-foreground">
            Skills you create or that are shared with you appear here.
          </p>
        </div>
      );
    }

    if (visibleSkills.length === 0) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
          <p className="m-0 text-xs font-medium text-foreground">No skills match your search</p>
          <p className="m-0 text-xs text-muted-foreground">
            Try another name or change the filter.
          </p>
        </div>
      );
    }

    return (
      <div className="min-h-0 flex-1 overflow-y-auto p-1" role="listbox" aria-label="Skills">
        {visibleSkills.map((skill) => {
          const selected = selectedIds.has(skill.id) || selectedSlugs.has(skill.slug);
          const unavailable = !skill.enabled || (!selected && selectionAtLimit);
          return (
            <button
              key={`${skill.source}-${skill.id}`}
              type="button"
              role="option"
              aria-selected={selected}
              disabled={unavailable}
              onClick={() => {
                if (!selected && !unavailable) onSelect(skill);
                close("select");
              }}
              className={cn(
                "flex w-full cursor-pointer flex-col items-start gap-0.5 overflow-hidden rounded-md border-0 bg-transparent px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus",
                unavailable && "cursor-not-allowed opacity-50 hover:bg-transparent"
              )}
              title={skill.description || skill.name}
            >
              <span className="w-full truncate text-xs font-medium text-foreground">
                /{skill.slug}
              </span>
              {skill.description && (
                <span className="w-full truncate text-xs text-muted-foreground">
                  {skill.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Skills"
      className="fixed z-[2147483647] flex flex-col overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-lg"
      style={{
        left: panelPosition.left,
        width: panelPosition.width,
        height: panelPosition.height,
        ...(panelPosition.side === "top"
          ? { bottom: panelPosition.verticalOffset }
          : { top: panelPosition.verticalOffset }),
      }}
    >
      <div className="flex h-12 min-h-12 items-center gap-2 p-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Back to attachment options"
          onClick={() => close("back")}
          className="h-8 w-8 shrink-0 cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>

        <label className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-border bg-background px-2 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary-muted">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="sr-only">Search Skills</span>
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search skills"
            className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>

        <SourceFilterMenu value={sourceFilter} onChange={onSourceFilterChange} />
      </div>

      {renderContent()}

      <button
        type="button"
        onClick={() => {
          close("explore");
          onExploreSkills();
        }}
        className="flex h-10 min-h-10 w-full cursor-pointer items-center border-0 border-t border-border bg-transparent px-3 text-left text-xs font-medium text-foreground hover:bg-accent"
      >
        Explore Skills
      </button>
    </div>,
    document.body
  );
};
